/**
 * batch-65 finqle-verzamelbon (FO-484) - de PURE regels van de verzamelbon.
 *
 * Samengevoegde werkbonnen gaan als één verzamelbon naar Finqle: één document
 * met één kop, één regeltabel, één totaal en één akkoordblok, de bonnen als
 * bijlagepagina's. Hier staat alles wat zonder database te beslissen is:
 *
 *   - het NUMMER: `VB-{jaar}-{volgnummer}`, `-{deelNr}` per deel van een
 *     gesplitste zending, `-{revisie}` vanaf revisie 2 (deel-afkeuring door de
 *     klant, of een bewuste herverzending van dezelfde bonnen);
 *   - het HERKENNEN van verzamelbonnummers in vrije tekst (Finqle-matcher);
 *   - de TOTALEN: in hele centen (exact, geen float-drift), btw over het
 *     documenttotaal, afgerond pas aan het eind (CLAUDE.md "Geld en getallen");
 *   - de EENHEID per regelcategorie en de periode-afleiding van een bundel.
 *
 * De API vertaalt aan de rand van/naar Prisma `Decimal` (zoals `indexatie.ts`).
 */
import type { VerzamelbonRoute, VerzamelbonVariant } from '@streep/types'
import { mondayOfIsoWeek, parseWeekString, weekStringFromDate } from './iso-week'
import { facturabeleUren, factuurPauzeMinutenVan, type OudeFacturatiePauze } from './werkbon-hours'
import { vangnetFactuurpauzeMinuten, type PauzeVerloning } from './werkbon-pauze'

/** Vast voorvoegsel van een verzamelbonnummer (eigen reeks, geen factuurnummer). */
export const VERZAMELBON_PREFIX = 'VB'

/** Minimale breedte van het volgnummer (VB-2026-0001); groter mag gewoon doorlopen. */
export const VERZAMELBON_VOLGNUMMER_BREEDTE = 4

/** `VB-2026-0042`: het basisnummer van een zending. */
export function formatVerzamelbonBasis(jaar: number, volgnummer: number): string {
  if (!Number.isInteger(jaar) || jaar < 2000 || jaar > 9999) {
    throw new Error(`Ongeldig jaar voor een verzamelbonnummer: ${jaar}`)
  }
  if (!Number.isInteger(volgnummer) || volgnummer < 1) {
    throw new Error(`Ongeldig volgnummer voor een verzamelbonnummer: ${volgnummer}`)
  }
  return `${VERZAMELBON_PREFIX}-${jaar}-${String(volgnummer).padStart(VERZAMELBON_VOLGNUMMER_BREEDTE, '0')}`
}

/**
 * Het stamnummer van één deel: het basisnummer, met `-{deelNr}` zodra de zending
 * door de mailgrootte in meer dan één deel valt (spec §6.1: elk deel is een eigen
 * verzamelbon met een eigen totaal).
 */
export function verzamelbonStamNummer(basis: string, deelNr: number, aantalDelen: number): string {
  return aantalDelen > 1 ? `${basis}-${deelNr}` : basis
}

/**
 * Het volledige nummer: het stamnummer, met `-{revisie}` vanaf revisie 2
 * ("zelfde zending + -2" na een deel-afkeuring door de klant, spec §C.6).
 */
export function verzamelbonNummer(stamNummer: string, revisie: number): string {
  return revisie > 1 ? `${stamNummer}-${revisie}` : stamNummer
}

/**
 * Verzamelbonnummers in vrije tekst (onderwerp, PO-nummer, regelomschrijving van
 * een Finqle-verzoek). Hoofdletter-ongevoelig; genormaliseerd naar hoofdletters,
 * ontdubbeld. Bewust exact: de caller zoekt het nummer letterlijk op, zodat
 * `VB-2026-0042-2` nooit stil als `VB-2026-0042` telt.
 */
const VERZAMELBON_IN_TEKST = /\bVB-(\d{4})-(\d{4,9})((?:-\d{1,3}){0,2})\b/gi

export function vindVerzamelbonNummers(velden: ReadonlyArray<string | null | undefined>): string[] {
  const gevonden = new Set<string>()
  for (const veld of velden) {
    if (!veld) continue
    for (const m of veld.matchAll(VERZAMELBON_IN_TEKST)) {
      gevonden.add(`${VERZAMELBON_PREFIX}-${m[1]}-${m[2]}${m[3] ?? ''}`)
    }
  }
  return [...gevonden]
}

// ─── Geld in hele centen ──────────────────────────────────────────────────────

/**
 * Een bedrag naar hele centen (half van nul af). De invoer is een getal uit de
 * gedeelde regelbouwer (`buildInvoiceLines`) of een Decimal-string; deze ene
 * afronding per regel is de rand waar het document zijn bedragen vastlegt.
 */
export function verzamelbonCenten(bedrag: number | string): number {
  const n = typeof bedrag === 'number' ? bedrag : Number(bedrag)
  if (!Number.isFinite(n)) throw new Error(`Ongeldig bedrag: ${String(bedrag)}`)
  // toFixed(6) haalt binaire ruis weg (0.1 + 0.2) voordat we op de cent afronden.
  const schoon = Number(n.toFixed(6))
  const centen = Math.round(Math.abs(schoon) * 100)
  return schoon < 0 ? -centen : centen
}

/** Centen -> "1234.56" (Decimal-string voor Prisma). */
export function verzamelbonCentenNaarDecimaal(centen: number): string {
  if (!Number.isInteger(centen)) throw new Error(`Geen hele centen: ${centen}`)
  const teken = centen < 0 ? '-' : ''
  const abs = Math.abs(centen)
  return `${teken}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

// BigInt-constanten via `BigInt(...)` i.p.v. literals (`0n`): de frontend-apps
// compileren `@streep/utils` onder target ES2017 (zelfde keuze als indexatie.ts).
const NUL = BigInt(0)
const EEN = BigInt(1)
const TWEE = BigInt(2)
const TIENDUIZEND = BigInt(10000)

/** Percentage (21, "21.00", 9) -> honderdsten van een procent (2100), exact. */
function percentageInHonderdsten(percentage: number | string): bigint {
  const n = typeof percentage === 'number' ? percentage : Number(percentage)
  if (!Number.isFinite(n) || n < 0) throw new Error(`Ongeldig btw-percentage: ${String(percentage)}`)
  return BigInt(Math.round(n * 100))
}

/** Deel met afronding half van nul af (BigInt). */
function deelAfgerond(teller: bigint, noemer: bigint): bigint {
  const negatief = teller < NUL
  const abs = negatief ? -teller : teller
  const q = abs / noemer
  const r = abs % noemer
  const afgerond = r * TWEE >= noemer ? q + EEN : q
  return negatief ? -afgerond : afgerond
}

export interface VerzamelbonTotalen {
  /** Som van de regelbedragen, in centen. */
  exclBtwCenten: number
  btwCenten: number
  inclBtwCenten: number
}

/**
 * Het ÉNE totaal van een verzamelbon: de som van de (op de cent vastgelegde)
 * regelbedragen, btw over het documenttotaal (niet per bon opgeteld: dan zou het
 * document afwijken van wat Finqle er zelf van maakt), incl. = excl. + btw.
 * `btwVerlegd` = 0% ongeacht het percentage.
 */
export function berekenVerzamelbonTotalen(args: {
  regelCenten: ReadonlyArray<number>
  btwPercentage: number | string
  btwVerlegd: boolean
}): VerzamelbonTotalen {
  let excl = 0
  for (const c of args.regelCenten) {
    if (!Number.isInteger(c)) throw new Error(`Regelbedrag niet in hele centen: ${c}`)
    excl += c
  }
  const btw = args.btwVerlegd
    ? 0
    : Number(deelAfgerond(BigInt(excl) * percentageInHonderdsten(args.btwPercentage), TIENDUIZEND))
  return { exclBtwCenten: excl, btwCenten: btw, inclBtwCenten: excl + btw }
}

// ─── Weergave ─────────────────────────────────────────────────────────────────

/**
 * FO-322 op de regeltabel: de gedeelde regelbouwer zet de VOLLEDIGE monteursnaam
 * in de omschrijving ("Service Jan Jansen - Week 38"). De verzamelbon is klant-
 * facing en de bijlagepagina's tonen "Jan J." zonder NAW-toestemming; de regel
 * volgt dus dezelfde weergave, anders lekt de tabel wat de bijlage afschermt.
 * Langste naam eerst, zodat een kortere naamgenoot nooit half vervangt.
 */
export function vervangMonteurNamen(
  omschrijving: string,
  namen: ReadonlyArray<{ volledig: string; klant: string }>,
): string {
  const gesorteerd = [...namen]
    .filter((n) => n.volledig.trim() !== '' && n.volledig !== n.klant)
    .sort((a, b) => b.volledig.length - a.volledig.length)
  let uit = omschrijving
  for (const n of gesorteerd) uit = uit.split(n.volledig).join(n.klant)
  return uit
}

/** Regelcategorieën die in uren tellen; de rest is een post (aantal x bedrag). */
const UUR_CATEGORIEEN: ReadonlySet<string> = new Set([
  'ARBEID',
  'OVERUREN',
  'REISTIJD',
  'TOESLAG',
  'CONSIGNATIE_OPROEP',
  // batch-68 controle (FO-343 §4.3/§4.4) - in uren, tegen het uurtarief.
  'MINIMUMAFNAME',
  'MEERWERK',
])

/** De eenheid in de regeltabel: "uur" of "post" (km zit in de omschrijving). */
export function verzamelbonEenheid(categorie: string): 'uur' | 'post' {
  return UUR_CATEGORIEEN.has(categorie) ? 'uur' : 'post'
}

/**
 * Periode van een bundel = van de eerste tot de laatste werkbondatum (spec §6.3:
 * bonnen uit verschillende weken mogen, de kop toont het bereik). Null zonder bonnen.
 */
export function verzamelbonPeriode(
  bonnen: ReadonlyArray<{ startDatum: Date; eindDatum: Date }>,
): { van: Date; tot: Date } | null {
  let van: Date | null = null
  let tot: Date | null = null
  for (const b of bonnen) {
    if (!van || b.startDatum < van) van = b.startDatum
    if (!tot || b.eindDatum > tot) tot = b.eindDatum
  }
  return van && tot ? { van, tot } : null
}

/**
 * De ISO-week als een bundel binnen één week valt ("4 werkbonnen, week 38"),
 * anders null (dan toont de UI het datumbereik). Werkbondatums zijn kalenderdagen
 * (UTC-middernacht), dus de UTC-week is de NL-week.
 */
export function verzamelbonWeek(van: Date, tot: Date): { week: number; jaar: number } | null {
  const a = weekStringFromDate(van)
  const b = weekStringFromDate(tot)
  if (a !== b) return null
  const [week, jaar] = a.split('-').map((d) => Number(d))
  return week && jaar ? { week, jaar } : null
}

// ─── Bundelperiode per klant (spec §D) ───────────────────────────────────────

export type BundelPeriodeSoort = 'WEEK' | 'MAAND'

const NL_MAANDEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
] as const

export interface BundelPeriode {
  /** Stabiele sleutel: 'W2026-38' of 'M2026-09'. */
  sleutel: string
  /** "Week 38, 2026" of "september 2026". */
  label: string
  /** Eerste en laatste kalenderdag van de periode (yyyy-MM-dd, NL). */
  van: string
  tot: string
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/**
 * De bundelperiode van één werkbon: bij WEEK de ISO-week van de bon (`weekNr`,
 * 'ww-jjjj', de canonieke week op de werkbon), bij MAAND de kalendermaand van de
 * laatste werkdag (`eindDatum`, een kalenderdag).
 */
export function bundelPeriodeVan(
  bon: { weekNr: string; eindDatum: Date },
  soort: BundelPeriodeSoort,
): BundelPeriode {
  if (soort === 'WEEK') {
    const { week, year } = parseWeekString(bon.weekNr)
    const maandag = mondayOfIsoWeek(week, year)
    const zondag = new Date(maandag.getTime() + 6 * 86_400_000)
    return {
      sleutel: `W${year}-${String(week).padStart(2, '0')}`,
      label: `Week ${week}, ${year}`,
      van: ymd(maandag),
      tot: ymd(zondag),
    }
  }
  const jaar = bon.eindDatum.getUTCFullYear()
  const maand = bon.eindDatum.getUTCMonth()
  const eerste = new Date(Date.UTC(jaar, maand, 1))
  const laatste = new Date(Date.UTC(jaar, maand + 1, 0))
  return {
    sleutel: `M${jaar}-${String(maand + 1).padStart(2, '0')}`,
    label: `${NL_MAANDEN[maand]} ${jaar}`,
    van: ymd(eerste),
    tot: ymd(laatste),
  }
}

export interface BundelVoorstelBon {
  id: string
  werkbonNumber: string
  clientId: string
  entityId: string
  weekNr: string
  eindDatum: Date
}

export interface BundelVoorstel<T extends BundelVoorstelBon> {
  /** `clientId:entityId:periodeSleutel`. */
  sleutel: string
  clientId: string
  entityId: string
  soort: BundelPeriodeSoort
  periode: BundelPeriode
  /** De periode is voorbij (laatste dag < vandaag): de bundel is compleet. */
  afgesloten: boolean
  bonnen: T[]
}

/**
 * Het automatische bundelvoorstel (spec §D.8): per klant x BV x periode de bonnen
 * die nog naar Finqle moeten, voor klanten die per week of per maand gebundeld
 * willen. Klanten zonder periode (PER_BON) doen niet mee. Volgorde: afgesloten
 * perioden eerst (die kunnen nu weg), dan op periode en werkbonnummer.
 */
export function groepeerBundelVoorstellen<T extends BundelVoorstelBon>(
  bonnen: ReadonlyArray<T>,
  periodePerKlant: ReadonlyMap<string, BundelPeriodeSoort>,
  vandaagYmd: string,
): BundelVoorstel<T>[] {
  const groepen = new Map<string, BundelVoorstel<T>>()
  for (const bon of bonnen) {
    const soort = periodePerKlant.get(bon.clientId)
    if (!soort) continue
    let periode: BundelPeriode
    try {
      periode = bundelPeriodeVan(bon, soort)
    } catch {
      // Onleesbaar weeknummer: geen voorstel voor deze bon (de losse flow blijft).
      continue
    }
    const sleutel = `${bon.clientId}:${bon.entityId}:${periode.sleutel}`
    const bestaand = groepen.get(sleutel)
    if (bestaand) {
      bestaand.bonnen.push(bon)
    } else {
      groepen.set(sleutel, {
        sleutel,
        clientId: bon.clientId,
        entityId: bon.entityId,
        soort,
        periode,
        afgesloten: periode.tot < vandaagYmd,
        bonnen: [bon],
      })
    }
  }
  const lijst = [...groepen.values()]
  for (const g of lijst) g.bonnen.sort((a, b) => a.werkbonNumber.localeCompare(b.werkbonNumber, 'nl'))
  lijst.sort(
    (a, b) =>
      Number(b.afgesloten) - Number(a.afgesloten) ||
      a.periode.van.localeCompare(b.periode.van) ||
      a.sleutel.localeCompare(b.sleutel),
  )
  return lijst
}

/**
 * Welke revisie volgt? De hoogste bekende revisie van de stamlijn + 1 (een
 * revisie die ooit is uitgegeven - ook een vervangen - wordt nooit hergebruikt).
 */
export function volgendeVerzamelbonRevisie(bekendeRevisies: ReadonlyArray<number>): number {
  return bekendeRevisies.reduce((max, r) => (r > max ? r : max), 1) + 1
}

/**
 * Is de set bonnen van een nieuw deel exact die van een bestaande verzamelbon?
 * Dan is het nieuwe document een REVISIE (zelfde stamnummer, `-{revisie}`) en geen
 * nieuw nummer (spec §6.4 "nieuw verzamelbonnummer met suffix").
 */
export function zelfdeBonnen(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  if (set.size !== a.length) return false
  return b.every((id) => set.has(id))
}

// ─── Documentvariant: uren of bedragen (FO-507, batch-67) ────────────────────

export type VerzamelbonVariantWaarde = VerzamelbonVariant

/**
 * De variant per route (spec 207 §2.1): naar Finqle ALTIJD `UREN` - Finqle
 * prijst zelf en het document mag geen factuur zijn; een klantvoorkeur of een
 * expliciet verzoek om `MET_BEDRAGEN` wordt daar genegeerd (`afgedwongen`), nooit
 * stil: de caller logt het. Bij eigen facturatie (de ongenummerde specificatie
 * als bijlage bij een eigen factuur) wint een expliciet verzoek, dan de
 * klantinstelling, en anders blijft het `MET_BEDRAGEN` zoals voor b67 (T4).
 */
export function verzamelbonVariantVoorRoute(args: {
  route: VerzamelbonRoute
  klantVoorkeur?: VerzamelbonVariant | null
  gevraagd?: VerzamelbonVariant | null
}): { variant: VerzamelbonVariant; afgedwongen: boolean } {
  if (args.route === 'FINQLE') {
    const gewenst = args.gevraagd ?? args.klantVoorkeur ?? null
    return { variant: 'UREN', afgedwongen: gewenst === 'MET_BEDRAGEN' }
  }
  return { variant: args.gevraagd ?? args.klantVoorkeur ?? 'MET_BEDRAGEN', afgedwongen: false }
}

/** Som van uren op twee decimalen, zonder float-drift (in honderdsten opgeteld). */
export function somUren(uren: ReadonlyArray<number>): number {
  let honderdsten = 0
  for (const u of uren) {
    if (!Number.isFinite(u)) throw new Error(`Ongeldig urengetal: ${String(u)}`)
    honderdsten += Math.round(u * 100)
  }
  return honderdsten / 100
}

/**
 * Regelcategorieën van de urenvariant waarvan `aantal` uren zijn en meetelt in
 * het urentotaal: werkuren per dag, reisuren en oproep-uren. Een standby-regel
 * (`CONSIGNATIE_STANDBY`, aantal = nachten) telt niet als uren.
 */
export const VERZAMELBON_UREN_TELLENDE_CATEGORIEEN: ReadonlySet<string> = new Set([
  'ARBEID',
  'REISTIJD',
  'CONSIGNATIE_OPROEP',
])

/** De eenheid van een urenregel: uren, of nachten voor de standby-periode. */
export function verzamelbonUrenEenheid(categorie: string): 'uur' | 'nacht' {
  return categorie === 'CONSIGNATIE_STANDBY' ? 'nacht' : 'uur'
}

/** Het urentotaal van een set urenregels (alleen de uren-tellende categorieën). */
export function verzamelbonUrenTotaal(
  regels: ReadonlyArray<{ categorie: string; aantal: number }>,
): number {
  return somUren(regels.filter((r) => VERZAMELBON_UREN_TELLENDE_CATEGORIEEN.has(r.categorie)).map((r) => r.aantal))
}

// ─── Uren en factuurpauze van één dagregel (integrator-review batch-67) ─────

/** De dagregel-velden van de facturatie-as (Prisma-rij of DTO; Decimal als number). */
export interface VerzamelbonDagregelFacturatieInput {
  /** De opgeslagen BETAALDE uren (`WerkbonDayEntry.hours`). */
  hours: number
  date?: Date | string | null
  pauzeMinutes?: number | null
  pauzeVerloning?: PauzeVerloning | null
  pauzeFacturatieMinuten?: number | null
}

/**
 * De facturabele uren en de factuurpauze van één dagregel zoals de verzamelbon
 * ze toont: in de urentabel, op de bijlagepagina's en (de uren) in het
 * klant-bundelitem. EÉN helper, zodat die nooit uit elkaar lopen
 * (integrator-review batch-67, review M3).
 *
 * - `uren` = de facturatie-as: `facturabeleUren`, met het vangnet van de oude
 *   schema-brede facturatiepauze voor een dagregel zonder eigen factuurpauze
 *   (precies de uren achter de facturatieregels);
 * - `pauzeMinuten` = wat er van die uren af ging: de eigen factuurpauze, of de
 *   verloningsaftrek plus het vangnet. Zo geldt begin - eind - pauze = uren.
 */
export function verzamelbonDagFacturatie(
  dag: VerzamelbonDagregelFacturatieInput,
  oudeFacturatiePauze: OudeFacturatiePauze | null,
): { uren: number; pauzeMinuten: number } {
  const uren = facturabeleUren(dag, oudeFacturatiePauze)
  const eigenFactuurpauze =
    typeof dag.pauzeFacturatieMinuten === 'number' &&
    Number.isFinite(dag.pauzeFacturatieMinuten) &&
    dag.pauzeFacturatieMinuten >= 0
  const vangnet = eigenFactuurpauze ? 0 : vangnetFactuurpauzeMinuten(oudeFacturatiePauze, dag.date, Number(dag.hours))
  return { uren, pauzeMinuten: factuurPauzeMinutenVan(dag) + vangnet }
}

// ─── Regels die de urenvariant niet kan dragen (integrator-review batch-67) ──

/**
 * Facturatieregel-categorieën die de urenvariant AFDEKT: ze volgen uit wat er
 * op het document staat (uren per dag met begin en eind, de dienst, reisuren,
 * kilometers, oproep-uren en standby-nachten), zodat Finqle ze met zijn eigen
 * contract kan prijzen. Bewust een allowlist: een nieuwe categorie (of een
 * VAST_BEDRAG/ADVIES/KORTING/OVERIG/CREDIT-regel) telt standaard als NIET
 * afgedekt, zodat hij nooit stil van het document verdwijnt.
 */
export const VERZAMELBON_UREN_GEDEKTE_CATEGORIEEN: ReadonlySet<string> = new Set([
  'ARBEID',
  'OVERUREN',
  'PLOEGENTOESLAG',
  'TOESLAG',
  'REISTIJD',
  'REISKOSTEN',
  'CONSIGNATIE_STANDBY',
  'CONSIGNATIE_OPROEP',
])

const NIET_UREN_LABELS: Record<string, string> = {
  MATERIAAL: 'materiaal',
  VAST_BEDRAG: 'vast bedrag',
  ADVIES: 'advies',
  KORTING: 'korting',
  OVERIG: 'overige kosten',
  CREDIT: 'credit',
  REISKOSTEN: 'reiskosten zonder kilometers',
}

/**
 * De categorieën van de regels van één werkbon die de urenvariant NIET kan
 * dragen: een regel met een bedrag (positief of negatief; 0 telt niet) buiten
 * de afgedekte categorieën, plus reiskosten als er op het document voor die bon
 * geen enkele kilometer staat (dan is er niets waarop Finqle ze kan prijzen).
 * Leeg = de bon kan zonder verlies als uren naar Finqle.
 */
export function verzamelbonNietUrenCategorieen(args: {
  regels: ReadonlyArray<{ categorie: string; bedragCenten: number }>
  urenRegels: ReadonlyArray<{ reisKm: number | null }>
}): string[] {
  const kmOpDocument = args.urenRegels.some((u) => (u.reisKm ?? 0) > 0)
  const uit = new Set<string>()
  for (const r of args.regels) {
    if (r.bedragCenten === 0) continue
    if (!VERZAMELBON_UREN_GEDEKTE_CATEGORIEEN.has(r.categorie)) uit.add(r.categorie)
    else if (r.categorie === 'REISKOSTEN' && !kmOpDocument) uit.add(r.categorie)
  }
  return [...uit]
}

/** "materiaal, vast bedrag" - leesbare opsomming voor de uitvalreden. */
export function verzamelbonNietUrenOmschrijving(categorieen: ReadonlyArray<string>): string {
  return categorieen.map((c) => NIET_UREN_LABELS[c] ?? c.toLowerCase().replace(/_/g, ' ')).join(', ')
}

/**
 * Geldtermen die op een UREN-verzamelbon NOOIT mogen voorkomen (spec 207 T3):
 * de renderer en de tests toetsen op deze lijst. Het losse euroteken staat er
 * expliciet in; "btw" ook in kleine letters, zodat een klant-btw-nummer in de kop
 * evenmin doorsijpelt. Integrator-review batch-67: ook betaalinstructies (IBAN,
 * betalen, betaling, betaaltermijn) - de spec eist "geen betaalinstructies".
 */
export const VERZAMELBON_GELD_TERMEN: ReadonlyArray<string> = [
  '€',
  'EUR',
  'factuur',
  'Factuur',
  'FACTUUR',
  'btw',
  'Btw',
  'BTW',
  'Tarief',
  'tarief',
  'Bedrag',
  'bedrag',
  'Subtotaal',
  'Totaal te betalen',
  'incl.',
  'excl.',
  'IBAN',
  'iban',
  'betaal',
  'Betaal',
  'BETAAL',
  'betalen',
  'Betalen',
  'betaling',
  'Betaling',
]

/** Komt een van de geldtermen in de tekst voor? Geeft de eerste treffer terug, anders null. */
export function vindGeldTerm(tekst: string): string | null {
  for (const term of VERZAMELBON_GELD_TERMEN) if (tekst.includes(term)) return term
  return null
}
